import type { ScheduleTRPCInput } from '@risksmart-app/trpc/src/types';
import type {
  InsertChildIndicatorInput,
  InsertIndicatorMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type InsertIndicatorTRPCInput = Omit<
  InsertChildIndicatorInput,
  'schedule' | 'Title' | 'Type' | 'ParentId'
> &
  ScheduleTRPCInput & {
    Title: string;
    Type: NonNullable<InsertChildIndicatorInput['Type']>;
    ParentId: string;
  };

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertIndicatorMutation => {
  return {
    insertChildIndicator: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertIndicatorTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.indicator.insert.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.indicator.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.indicator.indicatorsByParentId.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.control.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.register.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertIndicator: async (
      variables: InsertIndicatorTRPCInput
    ): Promise<InsertIndicatorMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
